import { useState, useEffect } from "react";
import { useCookieConsent, CookieConsent } from "../hooks/use-cookie-consent";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Shield, Gauge, BarChart3, Megaphone } from "lucide-react";

interface CookiePreferencesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: () => void;
}

export function CookiePreferencesDialog({
  open,
  onOpenChange,
  onSave,
}: CookiePreferencesDialogProps) {
  const { consent, updateConsent, acceptAll, rejectAll } = useCookieConsent();
  const [localConsent, setLocalConsent] = useState<CookieConsent>({
    essential: true,
    functional: false,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    if (consent) {
      setLocalConsent(consent);
    }
  }, [consent]);

  const handleToggle = (category: keyof CookieConsent) => {
    if (category === "essential") return; // Cannot disable essential cookies
    setLocalConsent((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSave = () => {
    updateConsent(localConsent);
    onSave?.();
  };

  const handleAcceptAll = () => {
    acceptAll();
    onSave?.();
  };

  const handleRejectAll = () => {
    rejectAll();
    onSave?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cookie Preferences</DialogTitle>
          <DialogDescription>
            Manage your cookie settings. Essential cookies cannot be disabled as
            they are required for the website to function properly.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Essential Cookies */}
          <div className="flex items-start justify-between gap-4 p-4 border rounded-lg bg-gray-50 dark:bg-gray-800/50">
            <div className="flex gap-3 flex-1">
              <Shield className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div className="space-y-1 flex-1">
                <Label htmlFor="essential" className="text-base font-semibold">
                  Essential Cookies
                </Label>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Required for authentication, security, and core functionality.
                  These cookies are necessary for the website to work and cannot
                  be disabled.
                </p>
              </div>
            </div>
            <Switch
              id="essential"
              checked={true}
              disabled
              aria-label="Essential cookies (always enabled)"
            />
          </div>

          {/* Functional Cookies */}
          <div className="flex items-start justify-between gap-4 p-4 border rounded-lg">
            <div className="flex gap-3 flex-1">
              <Gauge className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="space-y-1 flex-1">
                <Label htmlFor="functional" className="text-base font-semibold">
                  Functional Cookies
                </Label>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Remember your preferences, settings, and personalization
                  choices (e.g., theme, language, file history).
                </p>
              </div>
            </div>
            <Switch
              id="functional"
              checked={localConsent.functional}
              onCheckedChange={() => handleToggle("functional")}
              aria-label="Toggle functional cookies"
            />
          </div>

          {/* Analytics Cookies */}
          <div className="flex items-start justify-between gap-4 p-4 border rounded-lg">
            <div className="flex gap-3 flex-1">
              <BarChart3 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="space-y-1 flex-1">
                <Label htmlFor="analytics" className="text-base font-semibold">
                  Analytics Cookies
                </Label>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Help us understand how you use the site, which pages you
                  visit, and how we can improve performance and user experience.
                  We use Google Analytics and similar tools.
                </p>
              </div>
            </div>
            <Switch
              id="analytics"
              checked={localConsent.analytics}
              onCheckedChange={() => handleToggle("analytics")}
              aria-label="Toggle analytics cookies"
            />
          </div>

          {/* Marketing Cookies */}
          <div className="flex items-start justify-between gap-4 p-4 border rounded-lg">
            <div className="flex gap-3 flex-1">
              <Megaphone className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div className="space-y-1 flex-1">
                <Label htmlFor="marketing" className="text-base font-semibold">
                  Marketing Cookies
                </Label>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Used to deliver personalized ads and measure advertising
                  campaign effectiveness. These may track your activity across
                  different websites.
                </p>
              </div>
            </div>
            <Switch
              id="marketing"
              checked={localConsent.marketing}
              onCheckedChange={() => handleToggle("marketing")}
              aria-label="Toggle marketing cookies"
            />
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleRejectAll}
            className="sm:order-1"
          >
            Reject All
          </Button>
          <Button
            variant="outline"
            onClick={handleAcceptAll}
            className="sm:order-2"
          >
            Accept All
          </Button>
          <Button onClick={handleSave} className="sm:order-3">
            Save Preferences
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
